<script setup lang="ts">
import {computed, ref, watch} from "vue";
import {ChevronDown} from "@lucide/vue";
import MSSanteButton from "@/mssante/components/MSSanteButton.vue";
import MSSanteDialog from "@/mssante/components/MSSanteDialog.vue";
import {Button} from "@/core/components/ui/button";
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from "@/core/components/ui/dropdown-menu";
import {MSSanteAudience, MSSantePrefill, MSSanteSource, SurveySecureMessagingPrefill} from "@/mssante/interfaces/SecureMessaging";
import {secureMessagingApi} from "@/container";
import useSurveyMSSantePrefills from "@/mssante/composables/useSurveyMSSantePrefills";
import {Tooltip, TooltipContent, TooltipProvider, TooltipTrigger} from "@/core/components/ui/tooltip";

/**
 * Proposes a MSSanté message at the end of a survey, prefilled by the API from
 * the template attached to that survey (or a generic message when it has none).
 *
 * A single recipient renders a plain button; when the survey proposes both a
 * professional and a patient message, the button becomes a select asking who
 * to write to, and opens the dialog on the chosen message.
 */
const {surveyInstanceId, consumerKey, compact = false} = defineProps<{
    /** The completed survey answer instance to share. */
    surveyInstanceId?: number;
    /** The consumer the survey was answered for. */
    consumerKey?: string;
    /** Renders the logo alone, with the label moved to a tooltip. */
    compact?: boolean;
}>();

// Multi-root template: forward the attributes (e.g. a class harmonizing the
// button with its neighbors) to the rendered trigger explicitly.
defineOptions({inheritAttrs: false});

const {patient, toPrefills} = useSurveyMSSantePrefills(() => consumerKey);

/** The survey answer the message shares, what it is written from. */
const messageSource = computed((): MSSanteSource | undefined => surveyInstanceId
    ? {type: 'survey-answer', id: surveyInstanceId}
    : undefined);

const rendered = ref<SurveySecureMessagingPrefill[]>([]);

/** The messages to propose, in the order the API returned them. */
const prefills = computed((): MSSantePrefill[] => toPrefills(rendered.value));

/** Whether the survey proposes both a professional and a patient message. */
const selectMode = computed((): boolean => {
    return prefills.value.some(prefill => prefill.audience === 'patient')
        && prefills.value.some(prefill => prefill.audience !== 'patient');
});

/**
 * The single-recipient button label, named after the recipient. The MSSanté
 * logo already brands the button: the channel is only spelled out when no
 * recipient is named.
 */
const label = computed((): string => {
    const priority = rendered.value[0];

    if (priority?.audience === 'patient') {
        return 'Envoyer au patient';
    }

    return priority?.recipient_target === 'general_practitioner'
        ? 'Envoyer au médecin'
        : 'Informer';
});

/** The professional select option, named after the recipient hint. */
const professionalLabel = computed((): string => {
    const professional = rendered.value.find(prefill => prefill.audience !== 'patient');

    return professional?.recipient_target === 'general_practitioner'
        ? 'Envoyer au médecin'
        : 'Envoyer à un professionnel de santé';
});

const dialogOpen = ref<boolean>(false);
const dialogPrefills = ref<MSSantePrefill[]>([]);

/**
 * Open the dialog on the chosen audience's message; the other messages stay
 * reachable through the dialog's own « Envoyer aussi… » switch.
 */
const compose = (audience: MSSanteAudience) => {
    dialogPrefills.value = [
        ...prefills.value.filter(prefill => prefill.audience === audience),
        ...prefills.value.filter(prefill => prefill.audience !== audience),
    ];

    dialogOpen.value = true;
};

watch(() => surveyInstanceId, async (instanceId) => {
    rendered.value = [];

    if (!instanceId) {
        return;
    }

    try {
        rendered.value = await secureMessagingApi().surveyPrefill(instanceId);
    } catch {
        rendered.value = [];
    }
}, {immediate: true});
</script>

<template>
  <template v-if="prefills.length && selectMode">
    <DropdownMenu>
      <DropdownMenuTrigger as-child>
        <Button
          v-bind="$attrs"
          variant="outline"
          class="text-primary rounded-full"
        >
          <img src="@/assets/images/logos/logo-ms-sante.png" class="h-6 rounded-full" alt="MSSante">
          <template v-if="!compact">
            Envoyer le résultat
          </template>
          <ChevronDown class="size-4" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        <DropdownMenuItem @click="compose('professional')">
          {{ professionalLabel }}
        </DropdownMenuItem>
        <DropdownMenuItem @click="compose('patient')">
          Envoyer au patient
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
    <MSSanteDialog
      v-model:open="dialogOpen"
      :prefills="dialogPrefills"
      :consumer-key="consumerKey"
      :patient-name="patient?.information?.name"
      :customer-id="patient?.id"
      :source="messageSource"
    />
  </template>
  <TooltipProvider v-else-if="prefills.length && compact">
    <Tooltip :delay-duration="200">
      <TooltipTrigger as="span">
        <MSSanteButton
          v-bind="$attrs"
          compact
          class="rounded-full"
          :prefills="prefills"
          :consumer-key="consumerKey"
          :patient-name="patient?.information?.name"
          :customer-id="patient?.id"
        />
      </TooltipTrigger>
      <TooltipContent>
        {{ label }}
      </TooltipContent>
    </Tooltip>
  </TooltipProvider>
  <MSSanteButton
    v-else-if="prefills.length"
    v-bind="$attrs"
    class="rounded-full"
    :prefills="prefills"
    :consumer-key="consumerKey"
    :patient-name="patient?.information?.name"
    :customer-id="patient?.id"
  >
    {{ label }}
  </MSSanteButton>
</template>
